import type { Measurement, NutritionTotals, WorkoutSet } from '../types';

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function percent(value: number, target: number): number {
  if (!Number.isFinite(value) || !Number.isFinite(target) || target <= 0) return 0;
  return clamp(Math.round((value / target) * 100), 0, 999);
}

export function foodMacros(
  food: { kcal100g: number; protein100g: number; carbs100g: number; fat100g: number; fiber100g: number },
  amountG: number
): NutritionTotals {
  const ratio = Math.max(0, amountG) / 100;
  return {
    calories: round1(food.kcal100g * ratio),
    proteinG: round1(food.protein100g * ratio),
    carbsG: round1(food.carbs100g * ratio),
    fatG: round1(food.fat100g * ratio),
    fiberG: round1(food.fiber100g * ratio)
  };
}

export function rollingWeightAverage(
  rows: Array<Pick<Measurement, 'localDate' | 'weightKg'>>,
  windowSize = 7
): Array<{ date: string; weight: number; average: number }> {
  const clean = rows
    .filter((row): row is { localDate: string; weightKg: number } => typeof row.weightKg === 'number')
    .sort((a, b) => a.localDate.localeCompare(b.localDate));
  return clean.map((row, index) => {
    const slice = clean.slice(Math.max(0, index - windowSize + 1), index + 1);
    const average = slice.reduce((sum, item) => sum + item.weightKg, 0) / slice.length;
    return { date: row.localDate, weight: row.weightKg, average: round1(average) };
  });
}

export function workoutVolume(sets: WorkoutSet[]): number {
  return round1(
    sets.reduce((total, set) => {
      if (set.completed === false) return total;
      return total + (set.reps ?? 0) * (set.weightKg ?? 0);
    }, 0)
  );
}

export function formatDuration(minutes: number): string {
  const safe = Math.max(0, Math.round(minutes));
  const hours = Math.floor(safe / 60);
  const rest = safe % 60;
  if (!hours) return `${rest} 分`;
  return `${hours} 小時 ${rest} 分`;
}

export function round1(value: number): number {
  return Math.round((value + Number.EPSILON) * 10) / 10;
}

export function localDate(date = new Date(), timezone = 'Asia/Taipei'): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);
}

